import type { RouteRecordNormalized } from "vue-router";

import { useAuthStore } from "@/stores/auth";

/**
 * Single source of truth for "can the current user access this route?"
 *
 * Walks the matched chain and runs each record's `canAccess` predicate.
 * A route is accessible iff every record in the matched chain either has no
 * predicate or its predicate returns true.
 *
 * Inheritance is additive: a parent's `canAccess` applies to all
 * descendants; a descendant's own `canAccess` adds a narrower check on top.
 *
 * Used by:
 *   - navigationGuard (URL-level access enforcement)
 *   - SimpleSidebar (deciding which sidebar items to render)
 *
 * Caller is expected to have already short-circuited public routes,
 * authentication, and terms-acceptance checks.
 *
 * **Empty `matched` returns `true`** (vacuous truth: `[].every() === true`).
 * Safe today because both callers pass `to.matched` / `router.resolve(...).matched`
 * for paths derived from the route table itself — Vue Router always returns at
 * least one matching record (worst case: the catch-all 404 route, which is
 * marked `public: true` and never reaches this helper). If this utility is
 * ever reused with externally-supplied paths, the caller should reject empty
 * matched chains before passing them in.
 *
 * The `RouteMeta` interface (including the `canAccess` field) is augmented
 * in `frontend/src/types/vue-router.d.ts`.
 */
function canAccessRoute(matched: readonly RouteRecordNormalized[]): boolean {
  const passesOwnPredicates = matched.every(
    (record) => record.meta.canAccess === undefined || record.meta.canAccess()
  );
  if (!passesOwnPredicates) return false;

  /**
   * DENY-BY-DEFAULT FOR THE GRANT-AUDIT ROLE (RAP-588).
   *
   * Such an account is restricted to the data-sharing surface and is 403'd everywhere else by
   * the backend — so without this it kept a full sidebar of links that every one of them ended
   * in "Error Loading Data — Request failed with status code 403", and landed on exactly that
   * after login. Marking the one route it may reach, rather than adding a `canAccess` to every
   * other route, means a route added next quarter is fail-closed for this role without its
   * author knowing the role exists. That is the same reasoning as the backend's opt-in gate.
   *
   * Placed here rather than in the guard so the SIDEBAR obeys it too — both callers go through
   * this helper, which is why the menu and the URL enforcement cannot disagree.
   */
  const auth = useAuthStore();
  if (auth.isGrantAuditor) {
    return matched.some((record) => record.meta.allowGrantAudit === true);
  }

  /**
   * DENY-BY-DEFAULT FOR THE REFERRING PORTAL (RAP-630).
   *
   * For a flagged firm the portal REPLACES the app rather than adding to it (spec §3), so its
   * users may reach the `/portal` subtree and nothing else. Expressed here, for the same reason
   * as the audit block above: both callers go through this helper, so the sidebar and the URL
   * enforcement cannot disagree — and this is what reduces the nav to Leads · Capital · Cases ·
   * Settings without a second sidebar component existing.
   *
   * Placed AFTER the audit check so that if one principal is ever both, the narrower role wins.
   * An audit account is a handling firm's own single-screen login, which is a smaller surface
   * than the portal; resolving the other way would widen it.
   *
   * This is UX. It decides what renders, not what the API will serve — that is
   * `requireReferringPortal` on the backend, which no router can stand in for.
   */
  if (auth.isReferringPortalEnabled) {
    return matched.some((record) => record.meta.referringPortal === true);
  }

  return true;
}

export default canAccessRoute;
